import { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';

// Context
import { HostContext } from "../../context/HostContext";

// Components
import DashboardControls from './components/DashboardControls';
import DashboardListHeader from './components/DashboardListHeader';
import DashboardGroupHeader from './components/DashboardGroupHeader';
import DashboardSlideouts from './components/DashboardSlideouts';
import Breadcrumb from "../../container/breadcrumb";
import HeaderActions from "../../container/header_actions";
import PeriodBreadcrumb from "../../component/PeriodBreadcrumb";
import FilterPanel from '../../component/FilterPanel';
import InstanceTile from './InstanceTile';

// Hooks
import useDashboardState from '../../hooks/useDashboardState';
import useDashboardData from '../../hooks/useDashboardData';
import { useInstanceFilters } from '../../hooks/useInstanceFilters';

// Types
import InstanceTarget from '../../types/instance/InstanceTarget';
import { GroupByOption, SortByOption } from './types';
import HostTarget from '../../types/host/HostTarget';

// Constants
import { standardGroupByOptions, standardSortByOptions } from './constants';

// Styling
import './style.scss';

// Define props interface
interface IDashboardProps {
    toggleMenu: () => void;
    applyPeriod: Function
}

function Dashboard(props: IDashboardProps) {
    const { toggleMenu, applyPeriod } = props;
    const { hosts } = useContext(HostContext);

    // Use custom hooks
    const {
        searchTerm,
        setSearchTerm,
        filters,
        updateFilter,
        clearAllFilters,
        layoutMode,
        setLayoutMode,
        groupBy,
        setGroupBy,
        selectedTagNameForGrouping,
        setSelectedTagNameForGrouping,
        sortBy,
        setSortBy,
        selectedTagNameForSorting,
        setSelectedTagNameForSorting,
        sortAsc,
        setSortAsc,
        saveState,
        NUMERIC_SORT_COLUMNS
    } = useDashboardState();

    // Use instance filters hook
    const {
        instances,
        allHosts,
        datasourceIdToHostIdMap,
        datasourceHostTypeMap,
        availableFilterOptions,
        availableHostTypes,
        getSensorStatusLabel,
        filtersLoading: metricsLoading
    } = useInstanceFilters({
        shouldFetch: true
    });

    const {
        activityData,
        executionsData,
        eventCountsData,
        hostStatsMap
    } = useDashboardData(instances, datasourceIdToHostIdMap);

    // State for slideouts
    const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null);
    const [selectedHostDetails, setSelectedHostDetails] = useState<HostTarget | null>(null);
    const [selectedGroupName, setSelectedGroupName] = useState<string | null>(null);
    const [selectedGroupInstances, setSelectedGroupInstances] = useState<InstanceTarget[] | null>(null);
    const [showMultiGroupMessage, setShowMultiGroupMessage] = useState(false);

    // Compute available tag names from instances
    const availableTagNames = useMemo(() => {
        const tagNames = new Set<string>();
        instances.forEach(instance => {
            if (instance.tags) {
                Object.keys(instance.tags).forEach(tagName => tagNames.add(tagName));
            }
        });
        return Array.from(tagNames).sort();
    }, [instances]);

    // Tag options for dropdowns
    const tagOptions = useMemo(() => {
        return availableTagNames.map(tagName => ({
            label: tagName,
            value: `tag:${tagName}`
        }));
    }, [availableTagNames]);

    // Grouping options for the dropdown
    const groupByOptions = useMemo(() => {
        const eventsOptions = [
            {
                label: 'Alert',
                value: 'alerts'
            },
            {
                label: 'Change',
                value: 'changes'
            },
            {
                label: 'Custom',
                value: 'custom'
            }
        ];

        const tagsSubMenu = tagOptions.length > 0 ? [{
            label: 'Tags',
            value: 'tags-submenu',
            options: tagOptions
        }] : [];

        const eventsSubMenu = [{
            label: 'Events',
            value: 'events-submenu',
            options: eventsOptions
        }];

        return [...tagsSubMenu, ...eventsSubMenu, ...standardGroupByOptions];
    }, [tagOptions]);

    // Sorting options for the dropdown
    const sortByOptions = useMemo(() => {
        const eventsOptions = [
            {
                label: 'Alert',
                value: 'alerts'
            },
            {
                label: 'Change',
                value: 'changes'
            },
            {
                label: 'Custom',
                value: 'custom'
            }
        ];

        const tagsSubMenu = tagOptions.length > 0 ? [{
            label: 'Tags',
            value: 'tags-submenu',
            options: tagOptions
        }] : [];

        const eventsSubMenu = [{
            label: 'Events',
            value: 'events-submenu',
            options: eventsOptions
        }];

        return [...tagsSubMenu, ...eventsSubMenu, ...standardSortByOptions];
    }, [tagOptions]);

    // Handle Group By Dropdown Change
    const handleGroupByChange = (selectedValue: string) => {
        if (selectedValue.startsWith('tag:')) {
            const tagName = selectedValue.substring(4);
            setGroupBy('tag');
            setSelectedTagNameForGrouping(tagName);
        } else {
            setGroupBy(selectedValue as GroupByOption);
            setSelectedTagNameForGrouping(null);
        }
    };

    // Handle Sort By Dropdown Change
    const handleSortByChange = (selectedValue: string) => {
        if (selectedValue.startsWith('tag:')) {
            const tagName = selectedValue.substring(4);
            setSortBy('tag');
            setSelectedTagNameForSorting(tagName);
        } else {
            setSortBy(selectedValue as SortByOption);
            setSelectedTagNameForSorting(null);
        }
        const isNumeric = NUMERIC_SORT_COLUMNS.includes(selectedValue.startsWith('tag:') ? 'tag' : selectedValue);
        setSortAsc(!isNumeric);
    };

    // Handle Instance Selection for Slideout
    const handleInstanceSelect = (instanceId: number) => {
        setSelectedInstanceId(instanceId);
        setSelectedGroupName(null);
        setSelectedGroupInstances(null);

        const hostIds = datasourceIdToHostIdMap[instanceId];
        const hostDetails = hostIds && hostIds.length > 0 ? allHosts.find(h => h.id === hostIds[0]) : null;
        setSelectedHostDetails(hostDetails || null);
    };

    // Handle Group Selection for Slideout
    const handleGroupSelect = (groupName: string, groupInstances: InstanceTarget[]) => {
        setSelectedGroupName(groupName);
        setSelectedGroupInstances(groupInstances);
        setSelectedInstanceId(null);
        setSelectedHostDetails(null);
    };

    // Process and filter instances
    const processedInstances = useMemo(() => {
        let filteredInstances = instances;

        // 1. Filter by Search Term (case-insensitive)
        if (searchTerm) {
            filteredInstances = filteredInstances.filter(instance =>
                    instance.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by Sensor Status
        if (filters.sensorstatus && filters.sensorstatus.length > 0) {
            filteredInstances = filteredInstances.filter(instance =>
                    filters.sensorstatus.includes(getSensorStatusLabel(instance))
            );
        }


        // 3. Filter by Type
        if (filters.type && filters.type.length > 0) {
            filteredInstances = filteredInstances.filter(instance =>
                    instance.type && filters.type.includes(instance.type)
            );
        }

        // 4. Filter by Host Type (use datasourceHostTypeMap)
        if (filters.hostType && filters.hostType.length > 0) {
            filteredInstances = filteredInstances.filter(instance => {
                const hostTypes = datasourceHostTypeMap[instance.id]; // This is now an array
                if (!hostTypes || hostTypes.length === 0) return false;
                return hostTypes.some(hostType =>
                        filters.hostType.some(
                                filterType => filterType.toLowerCase().trim() === hostType.toLowerCase().trim()
                        )
                );
            });
        }

        // 5. Filter by Host
        if (filters.host && filters.host.length > 0) {
            filteredInstances = filteredInstances.filter(instance => {
                const hostIds = datasourceIdToHostIdMap[instance.id]; // This is now an array
                if (!hostIds || hostIds.length === 0) return false;
                return hostIds.some(hostId => {
                    const mappedHost = allHosts.find(h => h.id === hostId);
                    return mappedHost && filters.host.includes(mappedHost.name);
                });
            });
        }

        // 6. Filter by Tags (match key=value pairs)
        if (filters.tag && filters.tag.length > 0) {
            filteredInstances = filteredInstances.filter(instance => {
                if (!instance.tags) return false;
                // For each selected tag ("key=value"), check if instance.tags[key] === value
                return filters.tag.every(tag => {
                    const [key, value] = tag.split('=');
                    return instance.tags && instance.tags[key] === value;
                });
            });
        }

        // 7. Filter by Agent
        if (filters.agent && filters.agent.length > 0) {
            filteredInstances = filteredInstances.filter(instance =>
                instance.agentid && filters.agent.includes(instance.agentid.toString())
            );
        }

        // --- Map filtered instances to add metrics, event counts, hostType, hostStats, hostId ---
        const instancesWithData: InstanceTarget[] = filteredInstances.map(instance => {
            const instanceEventCounts = eventCountsData[instance.id]; // Get event counts
            const hostTypes = datasourceHostTypeMap[instance.id]; // Get host types from the map
            const hostIds = datasourceIdToHostIdMap[instance.id]; // Get host IDs from the map

            const primaryHostId = hostIds && hostIds.length > 0 ? hostIds[0] : undefined;
            const hostStats = primaryHostId !== undefined ? hostStatsMap[primaryHostId] : undefined;

            const instanceActivity = activityData[instance.id];
            const instanceExecutions = executionsData[instance.id];

            let metrics: InstanceTarget['metrics'] = undefined;
            if (instanceActivity || instanceExecutions) {
                const totalTime = instanceActivity?.waittime;
                const totalExecutions = instanceExecutions?.sum;
                let averageTime: number | undefined = undefined;
                if (totalTime !== undefined && totalExecutions !== undefined && totalExecutions > 0) {
                    averageTime = totalTime / totalExecutions;
                }
                metrics = {
                    totalTime: totalTime,
                    totalExecutions: totalExecutions,
                    averageTime: averageTime,
                    totalTimePrevious: undefined,
                    totalExecutionsPrevious: undefined,
                    averageTimePrevious: undefined
                };
            }
            return {
                ...instance,
                metrics,
                eventCounts: instanceEventCounts,
                hostType: hostTypes ? hostTypes.join(', ') : undefined, // Join types for display
                hostStats: hostStats,
                hostId: hostIds // Pass all host IDs
            };
        });

        // 7. Sort instances (now potentially with metrics and event counts)
        return [...instancesWithData].sort((a, b) => {
            let valA: any;
            let valB: any;
            switch (sortBy) {
                case 'name':
                    valA = a.name?.toLowerCase() || '';
                    valB = b.name?.toLowerCase() || '';
                    break;
                case 'type':
                    valA = a.type?.toLowerCase() || '';
                    valB = b.type?.toLowerCase() || '';
                    break;
                case 'host':
                    const getMappedHostNames = (inst: any) => {
                        if (inst.hostId && Array.isArray(inst.hostId)) {
                            return inst.hostId.map((id: number) => {
                                const mappedHost = allHosts.find(h => h.id === id);
                                return mappedHost ? mappedHost.name.toLowerCase() : '';
                            }).join(', ');
                        }
                        return '-';
                    };
                    valA = getMappedHostNames(a);
                    valB = getMappedHostNames(b);
                    break;
                case 'hostType':
                    valA = a.hostType?.toLowerCase() || '';
                    valB = b.hostType?.toLowerCase() || '';
                    break;
                case 'sensorstatus':
                    const getSensorStatusValue = (instance: InstanceTarget): number => {
                        if (instance.state?.toLowerCase() !== 'started') return 2; // Offline
                        if (instance.sensorstatus === 200) return 0; // Healthy
                        return 1; // Issue
                    };
                    valA = getSensorStatusValue(a);
                    valB = getSensorStatusValue(b);
                    break;
                case 'status':
                    const getDBStatusValue = (instance: InstanceTarget): number => {
                        if (instance.status === 200) return 0; // OK
                        if (instance.status === 0) return 1; // Unknown
                        return 2; // Other
                    };
                    valA = getDBStatusValue(a);
                    valB = getDBStatusValue(b);
                    break;
                case 'tag':
                    const tagA = selectedTagNameForSorting && a.tags ? a.tags[selectedTagNameForSorting] : undefined;
                    const tagB = selectedTagNameForSorting && b.tags ? b.tags[selectedTagNameForSorting] : undefined;
                    // Instances with the tag come first
                    if (tagA !== undefined && tagB === undefined) return sortAsc ? -1 : 1;
                    if (tagA === undefined && tagB !== undefined) return sortAsc ? 1 : -1;
                    valA = tagA?.toLowerCase() ?? '';
                    valB = tagB?.toLowerCase() ?? '';
                    break;
                case 'totalTime':
                    valA = a.metrics?.totalTime ?? 0;
                    valB = b.metrics?.totalTime ?? 0;
                    break;
                case 'executions':
                    valA = a.metrics?.totalExecutions ?? 0;
                    valB = b.metrics?.totalExecutions ?? 0;
                    break;
                case 'avgTime':
                    valA = a.metrics?.averageTime ?? 0;
                    valB = b.metrics?.averageTime ?? 0;
                    break;
                case 'alerts':
                    valA = a.eventCounts?.alert ?? 0;
                    valB = b.eventCounts?.alert ?? 0;
                    break;
                case 'changes':
                    valA = a.eventCounts?.change ?? 0;
                    valB = b.eventCounts?.change ?? 0;
                    break;
                case 'custom':
                    valA = a.eventCounts?.custom ?? 0;
                    valB = b.eventCounts?.custom ?? 0;
                    break;
                case 'cpu':
                    valA = a.hostStats?.cpu ?? 0;
                    valB = b.hostStats?.cpu ?? 0;
                    break;
                case 'disk':
                    valA = a.hostStats?.io ?? 0;
                    valB = b.hostStats?.io ?? 0;
                    break;
                case 'mem':
                    valA = a.hostStats?.memory ?? 0;
                    valB = b.hostStats?.memory ?? 0;
                    break;
                default:
                    return 0;
            }
            const safeValA = valA ?? (typeof valA === 'number' ? 0 : '');
            const safeValB = valB ?? (typeof valB === 'number' ? 0 : '');
            if (safeValA < safeValB) {
                return sortAsc ? -1 : 1;
            }
            if (safeValA > safeValB) {
                return sortAsc ? 1 : -1;
            }
            return 0;
        });
    }, [
        instances, searchTerm,
        filters.sensorstatus, filters.status, filters.type, filters.host, filters.tag, filters.hostType, filters.agent,
        sortBy, sortAsc, selectedTagNameForSorting,
        activityData, executionsData, eventCountsData,
        datasourceHostTypeMap, datasourceIdToHostIdMap, hostStatsMap, allHosts
    ]);

    // Group instances
    const groupedInstances = useMemo(() => {
        // If no grouping selected, return all instances under a single key
        if (groupBy === 'none' || (groupBy === 'tag' && !selectedTagNameForGrouping)) {
            return { 'All Instances': processedInstances };
        }

        return processedInstances.reduce((acc, instance) => {
            let groupKeys: string | string[] = 'Unknown'; // Default group key, can be a single string or an array of strings

            switch (groupBy) {
                case 'env':
                    // Existing logic (can be refined if needed)
                    const nameParts = instance.name.split('-');
                    groupKeys = nameParts.length > 1 ? nameParts[0] : 'Unknown Environment';
                    break;
                case 'type':
                    groupKeys = instance.type || 'Unknown Type';
                    break;
                case 'hostType':
                    groupKeys = instance.hostType ? instance.hostType.charAt(0).toUpperCase() + instance.hostType.slice(1) : 'Unknown OS';
                    break;
                case 'sensorstatus':
                    if (instance.state?.toLowerCase() !== 'started') {
                        groupKeys = 'Offline';
                    } else if (instance.sensorstatus === 200) {
                        groupKeys = 'OK';
                    } else {
                        groupKeys = 'Issue';
                    }
                    break;
                case 'status':
                    if (instance.status === 200) {
                        groupKeys = 'OK';
                    } else if (instance.status === 0) {
                        groupKeys = 'Unknown';
                    } else {
                        groupKeys = 'Other';
                    }
                    break;
                case 'host':
                    const hostIds = datasourceIdToHostIdMap[instance.id];
                    if (hostIds && hostIds.length > 0) {
                        groupKeys = hostIds.map(hostId => {
                            const mappedHost = allHosts.find(h => h.id === hostId);
                            return mappedHost && mappedHost.name ? mappedHost.name : 'Unmapped Host';
                        });
                    } else {
                        groupKeys = 'Unmapped Host';
                    }
                    break;
                case 'tag':
                    // Use the selected tag name to get the value
                    if (selectedTagNameForGrouping) { // Check if a tag name is selected
                        if (instance.tags && instance.tags[selectedTagNameForGrouping] !== undefined) {
                            const tagValue = instance.tags[selectedTagNameForGrouping];
                            // If the tag value is a comma-separated string, split it into an array
                            if (tagValue.includes(',')) {
                                groupKeys = tagValue.split(',').map(s => s.trim());
                            } else {
                                groupKeys = tagValue; // Otherwise, use the value as is
                            }
                        } else {
                            // If tags object exists but tag is missing, or tags object is missing entirely
                            groupKeys = `Tag '${selectedTagNameForGrouping}' not set`;
                        }
                    } else {
                        // Should not happen if groupBy is 'tag', but defensively set a default
                        groupKeys = 'Unknown Tag Group';
                    }
                    break;
                    // --- Add Event Grouping Cases ---
                case 'alerts':
                    const hasAlerts = (instance.eventCounts?.alert ?? 0) > 0;
                    groupKeys = hasAlerts ? 'Has Alerts' : 'No Alerts';
                    break;
                case 'changes':
                    const hasChanges = (instance.eventCounts?.change ?? 0) > 0;
                    groupKeys = hasChanges ? 'Has Change Events' : 'No Change Events';
                    break;
                case 'custom':
                    const hasCustom = (instance.eventCounts?.custom ?? 0) > 0;
                    groupKeys = hasCustom ? 'Has Custom Events' : 'No Custom Events';
                    break;
            }

            // Normalize groupKeys to an array of strings
            const finalGroupKeys = (Array.isArray(groupKeys) ? groupKeys : [groupKeys]).map(String);

            finalGroupKeys.forEach(key => {
                if (!acc[key]) {
                    acc[key] = [];
                }
                acc[key].push(instance);
            });

            return acc;
        }, {} as Record<string, InstanceTarget[]>);

    }, [processedInstances, groupBy, selectedTagNameForGrouping, allHosts, datasourceIdToHostIdMap]);

    // Check if any instance belongs to more than one group
    useEffect(() => {
        if (groupBy === 'none') {
            setShowMultiGroupMessage(false);
            return;
        }

        const instanceCounts = new Map<number, number>();
        Object.values(groupedInstances).forEach(group => {
            (group as InstanceTarget[]).forEach(instance => {
                instanceCounts.set(instance.id, (instanceCounts.get(instance.id) || 0) + 1);
            });
        });

        let isMultiGrouped = false;
        for (const count of instanceCounts.values()) {
            if (count > 1) {
                isMultiGrouped = true;
                break;
            }
        }
        setShowMultiGroupMessage(isMultiGrouped);
    }, [groupedInstances, groupBy]);

    // Save state on unmount
    useEffect(() => {
        return () => saveState();
    }, [saveState]);

    return (
            <div id="content" className="dashboard">
                <Helmet>
                    <title>Dashboard - DBmarlin</title>
                    <meta name="description" content="Dashboard of all monitored hosts with high-level performance statistics"/>
                </Helmet>

                {/* Heading */}
                <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                    <Breadcrumb heading="Instances" type="instances" instanceId={0} hostId={0}>
                        {[
                            <Link key="link-analysis" to="/dashboard">Analysis</Link>,
                            <Link key="link-dashboard" to="/dashboard" className="current-breadcrumb-link">Dashboard</Link>,
                            <PeriodBreadcrumb key="period-breadcrumb"/>
                        ]}
                    </Breadcrumb>
                    <HeaderActions toggleMenu={toggleMenu} refresh={() => {}} applyPeriod={applyPeriod}/>
                </div>

                {/* Main Content */}
                <div className="row">
                    <div className="col">
                        {/* Controls Bar */}
                        <DashboardControls
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                totalInstances={instances.length}
                                filteredInstances={processedInstances.length}
                                groupBy={groupBy}
                                handleGroupByChange={handleGroupByChange}
                                sortBy={sortBy}
                                handleSortByChange={handleSortByChange}
                                sortAsc={sortAsc}
                                setSortAsc={setSortAsc}
                                layoutMode={layoutMode}
                                setLayoutMode={setLayoutMode}
                                groupByOptions={groupByOptions}
                                sortByOptions={sortByOptions}
                                selectedTagNameForGrouping={selectedTagNameForGrouping}
                                selectedTagNameForSorting={selectedTagNameForSorting}
                        />

                        {/* Filter Panel */}
                        <FilterPanel
                                filterGroups={[
                                    {
                                        name: 'sensorstatus',
                                        label: 'Sensor Status',
                                        options: availableFilterOptions.sensorstatuses,
                                        selectedValues: filters.sensorstatus || [],
                                        onSelectionChange: (values: string[]) => updateFilter('sensorstatus', values)
                                    },
                                    {
                                        name: 'type',
                                        label: 'Type',
                                        options: availableFilterOptions.types,
                                        selectedValues: filters.type,
                                        onSelectionChange: (values: string[]) => updateFilter('type', values)
                                    },
                                    {
                                        name: 'tag',
                                        label: 'Tag',
                                        options: availableFilterOptions.tags,
                                        selectedValues: filters.tag,
                                        onSelectionChange: (values: string[]) => updateFilter('tag', values)
                                    },
                                    {
                                        name: 'host',
                                        label: 'Host',
                                        options: availableFilterOptions.hosts,
                                        selectedValues: filters.host,
                                        onSelectionChange: (values: string[]) => updateFilter('host', values)
                                    },
                                    {
                                        name: 'hostType',
                                        label: 'Host Type',
                                        options: availableHostTypes,
                                        selectedValues: filters.hostType,
                                        onSelectionChange: (values: string[]) => updateFilter('hostType', values)
                                    },
                                    {
                                        name: 'agent',
                                        label: 'Agent',
                                        options: availableFilterOptions.agents,
                                        selectedValues: filters.agent || [],
                                        onSelectionChange: (values: string[]) => updateFilter('agent', values)
                                    }
                                ]}
                                onClearAll={clearAllFilters}
                                isLoading={metricsLoading}
                        />

                        {showMultiGroupMessage && (
                                <div className="alert alert-info text-center" role="alert">
                                    Note that some databases are shown in multiple groups.
                                </div>
                        )}

                        {/* Instance Groups */}
                        <div className={`instance-groups layout-${layoutMode}`}>
                            {Object.entries(groupedInstances).map(([groupName, groupInstances]) => (

                                    <div className='instance-group-wrapper' key={groupName}>
                                        <div className="instance-group">
                                            {groupBy !== 'none' && (
                                                    <DashboardGroupHeader
                                                            groupName={groupName}
                                                            groupInstances={groupInstances as InstanceTarget[]}
                                                            onGroupSelect={handleGroupSelect}
                                                    />
                                            )}
                                            {layoutMode === 'list' && (groupInstances as InstanceTarget[]).length > 0 && (
                                                    <DashboardListHeader
                                                            sortBy={sortBy}
                                                            handleSortByChange={handleSortByChange}
                                                            sortAsc={sortAsc}
                                                            setSortAsc={setSortAsc}
                                                    />
                                            )}

                                            <div className={`instance-list-grid-body layout-${layoutMode}`}>
                                                {(groupInstances as InstanceTarget[]).map((instance: InstanceTarget) => (
                                                        <InstanceTile
                                                                key={instance.id}
                                                                instance={instance}
                                                                layoutMode={layoutMode}
                                                                onInstanceSelect={handleInstanceSelect}
                                                                hostType={instance.hostType}
                                                                allHosts={allHosts}
                                                                metricsDashInsteadOfNA
                                                        />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                            ))}
                            {processedInstances.length === 0 && instances.length > 0 && <p>No instances match the current filters.</p>}
                            {instances.length === 0 && <p>Loading instances or none available.</p>}
                        </div>

                        {/* Slideouts */}
                        <DashboardSlideouts
                                selectedInstanceWithMetrics={selectedInstanceId !== null ? processedInstances.find(inst => inst.id === selectedInstanceId) || null : null}
                                selectedHostDetails={selectedHostDetails}
                                hosts={hosts}
                                handleInstanceSlideoutClose={() => {
                                    setSelectedInstanceId(null);
                                    setSelectedHostDetails(null);
                                }}
                                selectedGroupName={selectedGroupName}
                                selectedGroupInstances={selectedGroupInstances}
                                handleGroupSlideoutClose={() => {
                                    setSelectedGroupName(null);
                                    setSelectedGroupInstances(null);
                                }}
                                applyPeriod={applyPeriod}
                        />
                    </div>
                </div>
            </div>
    );
}

export default Dashboard;
